import { useState } from "react";
import axios from "axios";
import { CheckCircle2 } from "lucide-react";
import Modal from "../Modal/Modal";
import Button from "../Button/Button";
import Select from "../Select/Select";
import Checkbox from "../Checkbox/Checkbox";
import { CREATE_SUPPORT_TICKET_URL } from "../../features/UrlHelper";
import { buildDiagnostics } from "../../features/diagnostics";

const CATEGORY_OPTIONS = [
  { value: "BUG", label: "Chyba" },
  { value: "QUESTION", label: "Otázka" },
  { value: "IMPROVEMENT", label: "Návrh na zlepšenie" },
  { value: "OTHER", label: "Iné" },
];

const MAX_SUBJECT = 150;
const MAX_DESCRIPTION = 4000;

function SupportTicketModal({ isOpen, onClose, screenshot, screenshotError, user, prevadzka }) {
  const [category, setCategory] = useState("BUG");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [attachScreenshot, setAttachScreenshot] = useState(true);

  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMessage, setErrorMessage] = useState("");
  const [ticketCode, setTicketCode] = useState("");

  const zavri = () => {
    onClose();
    // Reset až po zatvorení, aby používateľ nevidel, ako sa formulár vyprázdňuje.
    setTimeout(() => {
      setCategory("BUG");
      setSubject("");
      setDescription("");
      setAttachScreenshot(true);
      setStatus("idle");
      setErrorMessage("");
      setTicketCode("");
    }, 200);
  };

  const odosli = async (e) => {
    e.preventDefault();

    if (!subject.trim() || !description.trim()) {
      setErrorMessage("Vyplňte predmet aj popis chyby.");
      setStatus("error");
      return;
    }

    setStatus("sending");
    setErrorMessage("");

    try {
      const { data } = await axios.post(
        CREATE_SUPPORT_TICKET_URL,
        {
          category,
          subject: subject.trim(),
          description: description.trim(),
          pageUrl: window.location.href,
          ...(attachScreenshot && screenshot ? { screenshot } : {}),
          diagnostics: buildDiagnostics({
            userId: user?.userId ?? null,
            role: user?.userRole ?? null,
            screenshotError: screenshotError || null,
          }),
        },
      );

      setTicketCode(data.ticketCode);
      setStatus("success");
    } catch (err) {
      setErrorMessage(
        err?.response?.data?.message || "Hlásenie sa nepodarilo odoslať. Skúste to znova.",
      );
      setStatus("error");
    }
  };

  const odosielam = status === "sending";

  // Zakaznik nema ako vediet, ze tento formular ide tvorcovi webu a nie do
  // kuchyne (R5a). Bez tohto riadku by sem chodilo "neprisiel mi obed", co
  // Inver aj tak musi prepositat prevadzke -- a zakaznik zbytocne caka.
  const jeZakaznik = user?.userRole === "CUSTOMER";

  return (
    <Modal
      isOpen={isOpen}
      onClose={zavri}
      title={status === "success" ? "Hlásenie prijaté" : "Nahlásiť chybu"}
      size="large"
      className="support-widget__modal"
    >
      {status === "success" ? (
        <div className="support-widget__success" role="status">
          <CheckCircle2 size={48} aria-hidden="true" />
          <p className="support-widget__success-text">Ďakujeme, hlásenie sme prijali.</p>
          <p className="support-widget__code">
            Číslo tiketu: <strong>{ticketCode}</strong>
          </p>
          <p className="support-widget__hint">
            Číslo si poznačte — pomôže nám pri ďalšej komunikácii.
          </p>
          <Button variant="primary" onClick={zavri}>
            Zavrieť
          </Button>
        </div>
      ) : (
        <form className="support-widget__form" onSubmit={odosli}>
          {jeZakaznik && (
            <p className="support-widget__notice">
              Tento formulár slúži na <strong>chyby stránky</strong>. S objednávkou — čo vám
              prišlo, kedy dorazí, zmena či zrušenie — sa obráťte priamo na prevádzku
              {prevadzka?.phone ? (
                <>
                  , tel.{" "}
                  <a className="support-widget__phone" href={`tel:${prevadzka.phone}`}>
                    {prevadzka.phone}
                  </a>
                </>
              ) : null}
              .
            </p>
          )}

          <div className="support-widget__field">
            <Select
              label="Čoho sa hlásenie týka"
              options={CATEGORY_OPTIONS}
              value={category}
              onChange={setCategory}
            />
          </div>

          <div className="support-widget__field">
            <label className="support-widget__label" htmlFor="support-subject">
              Predmet
            </label>
            <input
              id="support-subject"
              className="support-widget__input"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={MAX_SUBJECT}
              placeholder="Stručne, čo sa stalo"
              disabled={odosielam}
              required
            />
          </div>

          <div className="support-widget__field">
            <label className="support-widget__label" htmlFor="support-description">
              Popis
            </label>
            <textarea
              id="support-description"
              className="support-widget__textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={MAX_DESCRIPTION}
              rows={5}
              placeholder="Čo ste robili, keď sa chyba objavila? Čo ste čakali, že sa stane?"
              disabled={odosielam}
              required
            />
          </div>

          {screenshot ? (
            <div className="support-widget__field">
              <Checkbox
                checked={attachScreenshot}
                onChange={setAttachScreenshot}
                label="Priložiť snímku obrazovky"
                disabled={odosielam}
              />
              {attachScreenshot && (
                <img
                  className="support-widget__preview"
                  src={screenshot}
                  alt="Náhľad snímky obrazovky"
                />
              )}
              <p className="support-widget__gdpr">
                Snímka môže obsahovať údaje zákazníkov. Použijeme ju výlučne na analýzu chyby
                a neukladáme ju.
              </p>
            </div>
          ) : (
            <p className="support-widget__notice" role="alert">
              {screenshotError || "Snímku obrazovky sa nepodarilo vytvoriť."} Hlásenie môžete
              odoslať aj bez nej.
            </p>
          )}

          {status === "error" && (
            <p className="support-widget__error" role="alert">
              {errorMessage}
            </p>
          )}

          <div className="support-widget__actions">
            <Button variant="ghost" type="button" onClick={zavri} disabled={odosielam}>
              Zrušiť
            </Button>
            <Button variant="primary" type="submit" disabled={odosielam}>
              {odosielam ? "Odosielam..." : status === "error" ? "Skúsiť znova" : "Odoslať hlásenie"}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

export default SupportTicketModal;
